import { config } from '@/configs/configPortal';
import { dispatchCustomEvent, EVENTS } from '@/helpers/adobe';
import { LIST_STATES_UF } from '@/helpers/constants';
import { isVehicleType } from '@/helpers/utils';
import { ListSelectOptionsProps, SelectOptionsProps } from '@/interfaces/filters';
import { useGeneralStore } from '@/providers/general-store-provider';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStore } from '../local.context';

export default function useFilters() {
    const {
        initialSelections,
        brands,
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedStateUF,
        selectedVersion,
        setSelectedBrand,
        setSelectedModel,
        setSelectedYear,
        setSelectedVersion,
        setSelectedStateUF,
    } = useLocalStore();
    const { vehicleType } = useGeneralStore();
    const type = isVehicleType(vehicleType, 'car') ? 'carros' : 'motos';
    const router = useRouter();
    const loadedData = useRef(false);

    const isBike = useMemo(() => isVehicleType(vehicleType, 'bike'), [vehicleType]);
    const currentUrl = config.urlTableFipe;

    const [models, setModels] = useState<ListSelectOptionsProps>([]);
    const [years, setYears] = useState<ListSelectOptionsProps>([]);
    const [versions, setVersions] = useState<ListSelectOptionsProps>([]);

    //Loading state
    const [isBrandLoading, setIsBrandLoading] = useState(false);
    const [isModelLoading, setIsModelLoading] = useState(false);
    const [isYearLoading, setIsYearLoading] = useState(false);
    const [isResultLoading, setIsResultLoading] = useState(false);

    useEffect(() => {
        if (initialSelections && !loadedData.current) {
            loadedData.current = true;
            setModels(initialSelections.modelsList?.list || []);
            setYears(initialSelections.yearsList || []);
            setVersions(initialSelections.versionsList || []);
            setSelectedBrand(initialSelections.brand);
            setSelectedModel(initialSelections.model);
            setSelectedYear(initialSelections.year);
            setSelectedStateUF(initialSelections.state);
            setSelectedVersion(initialSelections.version);
        }
    }, [initialSelections]);

    const dispatchEvent = useCallback((eventType: string, data: any) => {
        window.objDataLayer = { vehicle: { ...window.objDataLayer?.vehicle, ...data } };
        dispatchCustomEvent(eventType, { detail: window.objDataLayer });
    }, []);

    const handleBrandSelect = useCallback(
        async (option: SelectOptionsProps) => {
            dispatchEvent(EVENTS.SELECT_MARCA, { type: { name: type }, make: { name: option.label } });

            setSelectedBrand(option);
            setSelectedModel({ value: '', label: 'Selecione o modelo' });
            setSelectedYear({ value: '', label: 'Selecione o ano' });
            setSelectedVersion({ value: '', label: 'Selecione a versão' });
            setSelectedStateUF({ value: '', label: 'Selecione o estado' });

            setModels([]);
            setYears([]);

            setIsBrandLoading(true);
            try {
                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_BASE_URL}/api/filters/models?${new URLSearchParams({ type, brand: option.value })}`,
                );
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const modelsData = await response.json();
                    setModels(modelsData.list || []);
                } else {
                    const text = await response.text();
                    console.error('Models response is not JSON:', text);
                    setModels([]);
                }
            } finally {
                setIsBrandLoading(false);
            }

            if (!isBike) setVersions([]);
        },
        [dispatchEvent, type, isBike],
    );

    const handleModelSelect = useCallback(
        async (option: SelectOptionsProps) => {
            dispatchEvent(EVENTS.SELECT_MODELO, { model: { name: option.label } });

            setSelectedModel(option);
            setSelectedYear({ value: '', label: 'Selecione o ano' });
            setSelectedVersion({ value: '', label: 'Selecione a versão' });
            setSelectedStateUF({ value: '', label: 'Selecione o estado' });

            setYears([]);

            setIsModelLoading(true);
            try {
                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_BASE_URL}/api/filters/years?${new URLSearchParams({
                        type,
                        brand: selectedBrand!.value,
                        model: option.value,
                    })}`,
                );
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const yearsData = await response.json();
                    setYears(yearsData);
                } else {
                    const text = await response.text();
                    console.error('Years response is not JSON:', text);
                    setYears([]);
                }
            } finally {
                setIsModelLoading(false);
            }

            if (!isBike) setVersions([]);
        },
        [dispatchEvent, type, selectedBrand, isBike],
    );

    const handleYearSelect = useCallback(
        async (option: SelectOptionsProps) => {
            dispatchEvent(EVENTS.SELECT_ANO, { year: { model: { value: option?.label } } });

            setSelectedYear(option);
            setSelectedVersion({ value: '', label: 'Selecione a versão' });
            setSelectedStateUF({ value: '', label: 'Selecione o estado' });

            if (!isBike) {
                setVersions([]);

                setIsYearLoading(true);
                try {
                    const response = await fetch(
                        `${process.env.NEXT_PUBLIC_BASE_URL}/api/filters/versions?${new URLSearchParams({
                            type,
                            brand: selectedBrand!.value,
                            model: selectedModel!.value,
                            year: option.value,
                        })}`,
                    );
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        const versionsData = await response.json();
                        setVersions(versionsData);
                    } else {
                        const text = await response.text();
                        console.error('Versions response is not JSON:', text);
                        setVersions([]);
                    }
                } finally {
                    setIsYearLoading(false);
                }
            }
        },
        [dispatchEvent, type, selectedBrand, selectedModel, isBike],
    );

    const handleVersionSelect = useCallback(
        (option: SelectOptionsProps) => {
            dispatchEvent(EVENTS.SELECT_VERSAO, { version: { name: option.label } });

            setSelectedVersion(option);
            setSelectedStateUF({ value: '', label: 'Selecione o estado' });
        },
        [dispatchEvent, isBike],
    );

    const handleStateUFSelect = useCallback(
        (option: SelectOptionsProps) => {
            dispatchEvent(EVENTS.SELECT_ESTADO, { location: { state: option.label } });

            setSelectedStateUF(option);
        },
        [dispatchEvent],
    );

    const redirectResult = useCallback(() => {
        if (selectedModel.value && selectedBrand.value && selectedYear.value && (isBike || selectedVersion.value)) {
            let queryString: string = '';
            let isValidUF =
                LIST_STATES_UF.findIndex((uf) => uf.value.toUpperCase() === selectedStateUF?.value?.toUpperCase()) !==
                -1;
            if (selectedStateUF?.value?.toUpperCase() !== 'BR' && isValidUF) {
                queryString = new URLSearchParams({ uf: selectedStateUF.value }).toString();
            }
            setIsResultLoading(true);
            dispatchEvent(EVENTS.CONSULTAR_VALOR, {});

            let url = isBike
                ? `${currentUrl}/motos/${selectedBrand.value}/${selectedModel.value}/${selectedYear.value}`
                : `${currentUrl}/carros/${selectedBrand.value}/${selectedModel.value}/${selectedYear.value}/${selectedVersion?.value}`;

            if (selectedStateUF.value) {
                url = `${url}?uf=${selectedStateUF.value}`;
            }

            router.push(url.toLowerCase());
        }
    }, [
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedVersion,
        selectedStateUF,
        dispatchEvent,
        currentUrl,
        isBike,
    ]);

    return {
        brands: isBike ? brands.bike : brands.car,
        models,
        years,
        versions,
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedVersion,
        selectedStateUF,
        initialSelections,
        isBike,
        isBrandLoading,
        isModelLoading,
        isYearLoading,
        isResultLoading,
        handleBrandSelect,
        handleModelSelect,
        handleYearSelect,
        handleVersionSelect,
        handleStateUFSelect,
        redirectResult,
    };
}
